/**
 * General portal settings — the admin-console counterpart to Esri Portal's
 * own "General" page: the portal's name, its logo, and a description shown
 * on the home page.
 *
 * Shares its underlying data with `HomePageEditorTab` — both read and write
 * the SAME `HomePageConfig` document, just different fields of it, the same
 * way two tabs on one form would. Each save sends the WHOLE draft (this
 * tab's fields changed, every other field carried through unchanged), so
 * saving here can never wipe out what the Home Page tab configured, or the
 * other way around.
 */

import React, { useEffect, useState } from 'react';
import { FileText, Image as ImageIcon } from 'lucide-react';
import type { HomePageConfig } from '../../types';
import { settingsService } from '../../services/settingsService';
import ImagePicker from './ImagePicker';
import RichTextEditor from './RichTextEditor';

interface Props {
  config: HomePageConfig;
  onSaved: (config: HomePageConfig) => void;
}

/** Admin-only. Rendering is guarded by SettingsPage via `can('home:edit')`. */
export default function GeneralSettingsTab({ config, onSaved }: Props) {
  const [draft, setDraft] = useState<HomePageConfig>(config);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  useEffect(() => setDraft(config), [config]);

  const isDirty = JSON.stringify(draft) !== JSON.stringify(config);

  const update = <K extends keyof HomePageConfig>(key: K, value: HomePageConfig[K]) => {
    setDraft((current) => ({ ...current, [key]: value }));
    setSavedAt(null);
  };

  const save = async () => {
    if (!draft.title.trim()) {
      setError('Portal name cannot be empty');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const next = await settingsService.updateHomeConfig({
        ...draft,
        title: draft.title.trim(),
      });
      onSaved(next);
      setSavedAt(Date.now());
    } catch (failure) {
      setError(
        failure instanceof Error && failure.message
          ? failure.message
          : 'Could not save changes. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <section>
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
          <ImageIcon className="h-4 w-4 text-slate-400" />
          <h4 className="text-sm font-semibold text-[#0F2942]">Logo and Name</h4>
        </div>
        <div className="mt-4 space-y-4">
          <ImagePicker
            label="Logo"
            value={draft.logoUrl}
            onChange={(logoUrl) => update('logoUrl', logoUrl)}
            // A logo is small on screen, so 512px is generous; the 2 MB
            // ceiling is about what is worth READING, not what gets stored.
            maxBytes={2 * 1024 * 1024}
            maxDimension={512}
            targetBytes={120 * 1024}
            hint="Up to 2 MB — large files are resized automatically. Shown at the left of the home page header; the Blue/View mark and the MapGeoid footer are unaffected."
          />

          <label className="block">
            <span className="text-xs font-semibold text-slate-600">Name</span>
            <input
              value={draft.title}
              onChange={(event) => update('title', event.target.value)}
              className="mt-1.5 w-full rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none focus:border-teal-400 focus:ring-2 focus:ring-teal-100"
            />
            <span className="mt-1 block text-[11px] text-slate-400">
              Shown as the main heading on the home page — the hero itself is styled under Home Page.
            </span>
          </label>
        </div>
      </section>

      <section>
        <div className="flex items-center gap-2 border-b border-slate-100 pb-2.5">
          <FileText className="h-4 w-4 text-slate-400" />
          <h4 className="text-sm font-semibold text-[#0F2942]">Description</h4>
        </div>
        <p className="mt-2 text-[11px] text-slate-500">
          Introduce the portal, link out to documentation, or post a notice — with full formatting.
        </p>
        <div className="mt-3 space-y-2.5">
          <RichTextEditor
            value={draft.description ?? ''}
            onChange={(html) => update('description', html)}
            hint="Bold, colour, fonts, lists, links, images — or switch to HTML to write it directly."
          />
          <label className="flex items-center gap-2 text-xs font-medium text-slate-600">
            <input
              type="checkbox"
              checked={draft.showDescription ?? false}
              onChange={(event) => update('showDescription', event.target.checked)}
              className="h-3.5 w-3.5 accent-teal-600"
            />
            Show description toward the bottom of the home page
          </label>
        </div>
      </section>

      {error && (
        <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs font-medium text-red-600">
          {error}
        </p>
      )}
      {savedAt && !isDirty && (
        <p className="rounded-lg bg-teal-50 px-3 py-2 text-xs font-medium text-teal-700">
          Portal settings updated.
        </p>
      )}

      <div className="flex items-center gap-2 border-t border-slate-100 pt-4">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!isDirty || saving}
          className="rounded-lg bg-[#0F2942] px-4 py-2 text-sm font-semibold text-white transition hover:bg-[#16395C] disabled:opacity-40"
        >
          {saving ? 'Saving…' : 'Save changes'}
        </button>
        <button
          type="button"
          onClick={() => setDraft(config)}
          disabled={!isDirty || saving}
          className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 transition hover:bg-slate-50 disabled:opacity-40"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
